import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Booking } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { ChevronLeft, ChevronRight, Loader2, CheckCircle2, Wine, Music2, Clock } from 'lucide-react';
import { cn } from '../lib/utils';

function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function daysInMonth(d) { return new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate(); }

export default function BookingPage() {
  const { showId } = useParams();
  const [params] = useSearchParams();
  const prospectId = params.get('prospect') || '';
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [cursor, setCursor] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', topic: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    Booking.options(showId, prospectId || undefined).then((d) => {
      setData(d);
      // Pre-fill form if a prospect is attached
      if (d.prospect) {
        setForm(f => ({
          ...f,
          name: d.prospect.name || f.name,
          email: d.prospect.email || f.email,
          topic: f.topic || ''
        }));
      }
    }).catch(() => setErr(true));
  }, [showId, prospectId]);

  const slotsByDay = useMemo(() => {
    const map = {};
    (data?.slots || []).forEach((iso) => {
      const d = new Date(iso);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      (map[key] ||= []).push(iso);
    });
    return map;
  }, [data]);

  if (err) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#FAF7F2]">
        <div className="text-center">
          <div className="text-stone-500">Show not found</div>
          <Button variant="outline" className="mt-3" onClick={() => navigate('/')}>Home</Button>
        </div>
      </div>
    );
  }

  if (!data) {
    return <div className="min-h-screen grid place-items-center bg-[#FAF7F2]"><Loader2 className="w-6 h-6 animate-spin text-stone-400" /></div>;
  }

  const { show, durationMinutes, timezone, microsoftConnected, ignoreOutlookBusy } = data;
  const Icon = show.id === 'wine-talks' ? Wine : Music2;

  if (done) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#FAF7F2] p-6">
        <div className="max-w-md w-full bg-white rounded-xl border border-stone-200 p-8 text-center shadow-sm">
          <div className="w-14 h-14 rounded-full grid place-items-center mx-auto mb-4" style={{ background: `${show.color}1a`, color: show.color }}>
            <CheckCircle2 className="w-7 h-7" />
          </div>
          <h2 className="text-[22px] font-serif text-stone-900">You're booked!</h2>
          <p className="text-stone-600 text-[13.5px] mt-1.5">{new Date(done.guest.recordingDate).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })} — {show.name}</p>
          {done.emailSent && <p className="text-stone-500 text-[12.5px] mt-3">A confirmation has been sent to {done.guest.email}.</p>}
          {!done.emailSent && microsoftConnected === false && <p className="text-amber-600 text-[12.5px] mt-3">Note: confirmation email pending (host has not connected Outlook yet).</p>}
        </div>
      </div>
    );
  }

  const first = startOfMonth(cursor);
  const firstWeekday = first.getDay();
  const total = daysInMonth(cursor);
  const cells = [];
  for (let i = 0; i < firstWeekday; i++) cells.push(null);
  for (let d = 1; d <= total; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);
  const monthLabel = cursor.toLocaleString('en-US', { month: 'long', year: 'numeric' });
  const fmtKey = (d) => `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

  const todayKey = (() => { const t = new Date(); return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`; })();

  const daySlots = selectedDay ? (slotsByDay[selectedDay] || []) : [];

  const submit = async (e) => {
    e.preventDefault();
    if (!selectedSlot || !form.name || !form.email) return;
    setSubmitting(true);
    try {
      const res = await Booking.create(showId, { ...form, slot: selectedSlot }, prospectId || undefined);
      setDone(res);
    } catch (err) {
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF7F2] py-8 px-4">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center gap-3 mb-5">
          <div className="h-12 w-12 rounded-lg grid place-items-center text-white" style={{ background: show.color }}>
            <Icon className="w-5 h-5" />
          </div>
          <div>
            <div className="text-[12px] uppercase tracking-widest text-stone-500">Book a Recording</div>
            <h1 className="text-[26px] font-serif text-stone-900">{show.name}</h1>
          </div>
        </div>

        {data.prospect && data.prospect.status !== 'booked' && (
          <div className="mb-4 p-3 rounded-lg border border-stone-200 bg-white flex items-center gap-3">
            <div className="h-9 w-9 rounded-full grid place-items-center text-white font-semibold" style={{ background: show.color }}>
              {(data.prospect.name || '?')[0]}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[13.5px] font-medium text-stone-900">Hi {(data.prospect.name || '').split(' ')[0]} — welcome!</div>
              <div className="text-[11.5px] text-stone-500 truncate">Your info is pre-filled. Just pick a time below and we&apos;ll be set.</div>
            </div>
          </div>
        )}
        {data.prospect && data.prospect.status === 'booked' && (
          <div className="mb-4 p-3 rounded-lg border border-emerald-200 bg-emerald-50/50 text-[12.5px] text-emerald-900">
            ✓ You&apos;re already booked. If you need to reschedule, just reply to your confirmation email.
          </div>
        )}

        <div className="bg-white rounded-xl border border-stone-200 p-5 shadow-sm grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-[17px] font-serif text-stone-900">{monthLabel}</h3>
              <div className="flex items-center gap-1">
                <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} className="p-1.5 hover:bg-stone-100 rounded"><ChevronLeft className="w-4 h-4" /></button>
                <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} className="p-1.5 hover:bg-stone-100 rounded"><ChevronRight className="w-4 h-4" /></button>
              </div>
            </div>
            <div className="grid grid-cols-7 text-[10.5px] text-stone-500 font-semibold uppercase mb-1">
              {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => <div key={d} className="px-2 py-1 text-center">{d}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {cells.map((d, i) => {
                const cellKey = `book-${cursor.getFullYear()}-${cursor.getMonth()}-${i}`;
                if (!d) return <div key={cellKey} className="aspect-square" />;
                const key = fmtKey(d);
                const has = (slotsByDay[key] || []).length > 0;
                const isSel = selectedDay === key;
                const isPast = key < todayKey;
                return (
                  <button
                    key={cellKey}
                    disabled={!has || isPast}
                    onClick={() => { setSelectedDay(key); setSelectedSlot(null); }}
                    className={cn(
                      'aspect-square rounded-md text-[13px] font-medium transition-colors',
                      isSel ? 'text-white shadow-sm' : has ? 'bg-stone-100 hover:bg-stone-200 text-stone-900' : 'text-stone-300 cursor-not-allowed'
                    )}
                    style={isSel ? { background: show.color } : {}}
                  >
                    {d}
                  </button>
                );
              })}
            </div>
            <div className="mt-2 text-[11.5px] text-stone-500 flex items-center gap-1"><Clock className="w-3 h-3" /> {durationMinutes} min sessions • timezone: {timezone}</div>
            {ignoreOutlookBusy && (
              <div className="mt-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">Showing all times (overlaps with host's existing calendar are allowed).</div>
            )}
          </div>

          <div>
            {!selectedDay && (
              <div className="h-full grid place-items-center text-stone-400 text-[13px] min-h-[200px]">Select a date to see times</div>
            )}
            {selectedDay && !selectedSlot && (
              <div>
                <div className="text-[13px] font-medium text-stone-900 mb-2">Available times on {new Date(selectedDay).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</div>
                <div className="grid grid-cols-3 gap-2 max-h-[420px] overflow-auto pr-1">
                  {daySlots.map(iso => (
                    <button key={iso} onClick={() => setSelectedSlot(iso)} className="px-2 py-2 text-[12.5px] rounded border border-stone-200 hover:border-stone-400 hover:bg-stone-50">
                      {new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                    </button>
                  ))}
                  {daySlots.length === 0 && <div className="col-span-3 text-stone-400 text-[12.5px]">No openings.</div>}
                </div>
              </div>
            )}
            {selectedSlot && (
              <form onSubmit={submit} className="space-y-3">
                <div className="p-3 rounded-md bg-stone-50 border border-stone-200 flex items-center justify-between">
                  <div>
                    <div className="text-[11px] uppercase text-stone-500">Selected</div>
                    <div className="text-[13.5px] font-medium text-stone-900">{new Date(selectedSlot).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</div>
                  </div>
                  <button type="button" onClick={() => setSelectedSlot(null)} className="text-[12px] text-stone-500 hover:text-stone-900">change</button>
                </div>
                <div>
                  <Label>Your name</Label>
                  <Input value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} required />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input type="email" value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} required />
                </div>
                <div>
                  <Label>What would you like to talk about?</Label>
                  <Input value={form.topic} onChange={(e) => setForm(f => ({ ...f, topic: e.target.value }))} placeholder="Suggested episode topic" />
                </div>
                <div>
                  <Label>Anything else?</Label>
                  <Textarea rows={3} value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} />
                </div>
                <Button type="submit" disabled={submitting} className="w-full text-white" style={{ background: show.color }}>
                  {submitting ? 'Booking…' : 'Confirm booking'}
                </Button>
                {!microsoftConnected && (
                  <div className="text-[11px] text-amber-600 text-center">Note: host has not connected Outlook — your booking is saved but no email confirmation will be sent automatically.</div>
                )}
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
